import * as XLSX from 'xlsx';

export interface SolucionareRow {
  CNJ: string;
  codProcesso: string;
  Status: string;
  Descrição: string;
  Tribunal: string;
  Instância: string;
  Capas: number;
  Partes: number;
  Andamentos: number;
  Criado: string;
  Atualizado: string;
  'Última consulta': string;
  Deletado: string;
}

/**
 * Extrai valor numérico de contagem de múltiplas fontes possíveis no payload.
 */
function getCountValue(record: Record<string, unknown>, key: string): number {
  const variants = [
    key,
    key.toLowerCase(),
    `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
  ];
  const sources = [
    record,
    record.info as Record<string, unknown> | undefined,
    record.infoCounts as Record<string, unknown> | undefined,
    record.counts as Record<string, unknown> | undefined,
    (record.data as Record<string, unknown> | undefined)?.info as Record<string, unknown> | undefined,
    (record.data as Record<string, unknown> | undefined)?.counts as Record<string, unknown> | undefined,
  ];

  for (const source of sources) {
    if (!source) continue;

    for (const variant of variants) {
      const candidate = source[variant];
      if (candidate === undefined || candidate === null) continue;

      const normalized = typeof candidate === 'number' ? candidate : Number(candidate);
      if (!Number.isNaN(normalized)) return normalized;
    }
  }

  return 0;
}

function formatDate(val: unknown): string {
  if (!val) return '-';
  return new Date(String(val)).toLocaleString();
}

function getDescricao(record: Record<string, unknown>): string {
  const dp = record.dp as Record<string, unknown> | undefined;
  const dadosProcessos = record.DadosProcessos as Record<string, unknown> | undefined;
  return String(
    record.descricaoClassificacaoStatus ??
    dadosProcessos?.descricaoClassificacaoStatus ??
    dp?.descricaoClassificacaoStatus ??
    '-',
  );
}

/**
 * Converte um registro da API em linha plana para exportação.
 */
export function mapRowToExport(record: Record<string, unknown>): SolucionareRow {
  const dp = record.dp as Record<string, unknown> | undefined;
  const dadosProcessos = record.DadosProcessos as Record<string, unknown> | undefined;

  return {
    CNJ: String(record.cnj ?? '-'),
    codProcesso: String(record.codProcesso ?? dadosProcessos?.codProcesso ?? dp?.codProcesso ?? '-'),
    Status: String(record.status ?? 'NULL'),
    Descrição: getDescricao(record),
    Tribunal: String(record.siglaSistema ?? 'NULL'),
    Instância: String(record.instancia ?? '-'),
    Capas: getCountValue(record, 'capas'),
    Partes: getCountValue(record, 'partes'),
    Andamentos: getCountValue(record, 'andamentos'),
    Criado: formatDate(record.createdAt),
    Atualizado: formatDate(record.updatedAt),
    'Última consulta': record.dataUltimaConsultaProcesso
      ? String(record.dataUltimaConsultaProcesso)
      : '-',
    Deletado: record.deletedAt ? String(record.deletedAt) : '-',
  };
}

/**
 * Aplica largura automática às colunas com base no conteúdo.
 */
function autoFitColumns(sheet: XLSX.WorkSheet, rows: SolucionareRow[]): void {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]) as (keyof SolucionareRow)[];
  const colWidths = headers.map((header) => {
    const maxDataLen = rows.reduce((max, row) => {
      const val = String(row[header] ?? '');
      return Math.max(max, val.length);
    }, 0);
    return { wch: Math.max(header.length, maxDataLen) + 2 };
  });

  sheet['!cols'] = colWidths;
}

/**
 * Gera e faz download de um arquivo XLSX com os processos Solucionare filtrados.
 */
export function exportSolucionareToXlsx(rows: SolucionareRow[]): void {
  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.json_to_sheet(rows);
  autoFitColumns(sheet, rows);
  XLSX.utils.book_append_sheet(workbook, sheet, 'Processos');
  XLSX.writeFile(workbook, 'solucionare-processos.xlsx');
}
